import { useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { useDeleteTeamMutation, useGetTeamQuery } from '../../../api/teams'
import { NotFound } from '../../../components/ui-kit/NotFound'
import { routesProfile } from '../../../consts'
import { EditTeamTabs } from './EditTeamTabs'

export const EditTeam = () => {
  useLayoutEffect(() => {
    document.title = 'Редактирование команды'
  }, [])

  const { id } = useParams<{ id: string }>()
  const teamId = useMemo(() => (id ? parseInt(id, 10) : NaN), [id])
  const { data, isLoading, isError } = useGetTeamQuery({ id: teamId }, { skip: Number.isNaN(teamId) })
  const navigate = useNavigate()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteTeam, { isLoading: isDeletingTeam }] = useDeleteTeamMutation()

  const myTeamsPath = useMemo(() => `/${routesProfile.TEAMS.path}/mine`, [])

  if (Number.isNaN(teamId)) {
    return <NotFound text="Команда не найдена" />
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (isError || !data) {
    return <NotFound text="Команда не найдена" />
  }

  if (!data.canEditContent) {
    return <NotFound text="Нет доступа" />
  }

  const permissions = data.viewerPermissions
  const canDeleteTeam = permissions?.can_delete_team === true

  const handleConfirmDelete = async () => {
    try {
      await deleteTeam({ teamId }).unwrap()
      setIsDeleteModalOpen(false)
      toast.success('Команда удалена')
      navigate(myTeamsPath, { replace: true })
    } catch (err) {
      const status = (err as { status?: number; originalStatus?: number })?.status
        ?? (err as { originalStatus?: number })?.originalStatus
      if (status === 403) {
        toast.error('Нет прав удалить команду')
        return
      }
      if (status === 404) {
        toast.error('Команда не найдена или уже удалена')
        return
      }
      toast.error('Не удалось удалить команду')
    }
  }

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        },
      })}
    >
      <EditTeamTabs title="Редактирование команды" permissions={permissions} />

      {canDeleteTeam && (
        <>
          <Stack alignItems="center" px={1.5} width="100%" sx={{ mt: 2 }}>
            <Stack width="100%" maxWidth={{ xs: '736px', md: '840px' }}>
              <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
                <Stack gap={1.5}>
                  <Typography variant="black-16-semiBold">Опасная зона</Typography>
                  <Typography variant="black-14-normal" color="text.secondary">
                    Удаление команды необратимо. Перед удалением убедитесь, что вам больше не нужны
                    данные команды.
                  </Typography>
                  <Stack direction="row">
                    <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)}>
                      Удалить команду
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Stack>

          <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
            <DialogTitle>Удалить команду</DialogTitle>
            <DialogContent>
              <Typography variant="black-14-normal">
                Команда будет удалена и исчезнет из личного кабинета. Действие нельзя отменить.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="follow"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeletingTeam}
              >
                Отмена
              </Button>
              <Button variant="danger" onClick={() => void handleConfirmDelete()} disabled={isDeletingTeam}>
                {isDeletingTeam ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} color="inherit" />
                    <span>Удаление...</span>
                  </Stack>
                ) : (
                  'Удалить'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  )
}
